/**
 * Which `--cat-*` pair a plan category draws in — the ink and its wash.
 *
 * The one place the six categories meet the six colours, so the canon's own rule — "ta sama skala
 * barw wraca w filtrach i w kroku 2 kreatora" — is true by construction rather than by three
 * stylesheets agreeing. It is `shared/ui/` rather than a `model/` for the reason `muscleColour.ts`
 * gives beside it: a CSS custom-property name is a rendering fact, not a domain one, and
 * `entities/plan-draft` carries no colour at all.
 *
 * ── It hands back CUSTOM PROPERTIES, not class names, and that is what makes it shared ───────
 * Four surfaces draw a category and none of them draw it the same size: the table's chip is 24px
 * tall at 11px/800, the toolbar's filter pill is 30px at 12px, its active twin is 32px at 11.5/800,
 * and the create modal's option is 34px at 12.5/700. A component with four variants would be a prop
 * bag, and six `[data-category]` rules per stylesheet would be twenty-four rules that drift.
 *
 * So the colour arrives as two locals — `--cat-ink` and `--cat-wash` — and every stylesheet reads
 * `color: var(--cat-ink)` and `background: var(--cat-wash)`. Each surface keeps its own geometry,
 * the colour is decided once, and no stylesheet writes a literal (the Stylelint allowlist accepts a
 * token reference and nothing else, which is exactly what these are).
 *
 * ── `null` is a real category and gets a real colour ─────────────────────────────────────────
 * A plan whose category this build does not recognise still renders a chip, because the wire sent
 * SOMETHING and the library shows it as the server spelled it. It takes the Hybryda slate — the one
 * hue in the scale that makes no claim about the training — rather than a seventh colour, and
 * rather than an invisible chip.
 */

import type { CSSProperties } from 'react';

/** The six keys, structurally identical to `entities/plan-draft`'s `PlanCategoryKey`. */
export type PlanCategoryColourKey =
  | 'strength'
  | 'cardio'
  | 'hiit'
  | 'flexibility'
  | 'bodyweight'
  | 'hybrid';

const INK: Record<PlanCategoryColourKey, string> = {
  strength: 'var(--cat-strength)',
  cardio: 'var(--cat-cardio)',
  hiit: 'var(--cat-hiit)',
  flexibility: 'var(--cat-flexibility)',
  bodyweight: 'var(--cat-bodyweight)',
  hybrid: 'var(--cat-hybrid)',
};

const WASH: Record<PlanCategoryColourKey, string> = {
  strength: 'var(--cat-strength-wash)',
  cardio: 'var(--cat-cardio-wash)',
  hiit: 'var(--cat-hiit-wash)',
  flexibility: 'var(--cat-flexibility-wash)',
  bodyweight: 'var(--cat-bodyweight-wash)',
  hybrid: 'var(--cat-hybrid-wash)',
};

/**
 * The two locals a category's chip, pill, dot or option reads.
 *
 * Typed as `CSSProperties` through a cast, because React's own type has no member for a custom
 * property and the alternative — an index signature on every call site — would accept a typo in a
 * real CSS property too. The cast is on the one object this module builds.
 */
export function planCategoryColour(key: PlanCategoryColourKey | null): CSSProperties {
  const resolved = key ?? 'hybrid';
  return {
    '--cat-ink': INK[resolved],
    '--cat-wash': WASH[resolved],
  } as CSSProperties;
}
